<template>
  <div>
    <div class="el-input-group el-input-group--append el-input-group--prepend">
      <div class="oxygen-oxygen-selects">
        <el-select
          v-close-on-zoom
          :value="state.isCurrent"
          :disabled="readonly"
          class="current-select"
          @input="value => updateCurrent(value)"
        >
          <el-option :value="false" :label="'Was Oxygen given'" />
          <el-option :value="true" :label="'Is Oxygen being given'" />
        </el-select>
        <div class="oxygens-select-container">
          <el-select
            id="oxygen-oxygens-select"
            v-close-on-zoom
            :value="state.selectedOxygens"
            placeholder="any route"
            :filterable="true"
            :multiple="true"
            class="oxygen-routes-select"
            :disabled="readonly"
            @input="oxygenKeys => selectOxygens(oxygenKeys)"
          >
            <el-option
              v-for="oxygen in availableOxygens"
              :key="oxygen.id"
              :label="oxygen.name.en"
              :value="oxygen.id"
            />
          </el-select>
          <help-tooltip v-if="routeWarnings && routeWarnings.length" type="warning">
            <span v-for="warning in routeWarnings" :key="warning.message">
              {{ warning.message }}
              <br>
            </span>
          </help-tooltip>
        </div>
      </div>
      <div class="el-input-group__append">being given</div>
    </div>
    <dosage-count-check
      :disabled="readonly"
      :dosage-available="dosageAvailable"
      :count-available="countAvailable"
      :dosage="state.dosage"
      :count="state.count"
      :count-operator="state.countOperator"
      :is-cumulative="state.isCumulative"
      :count-warning="countWarning"
      class="dosage-count-check"
      @updateDosage="updateDosage"
      @updateCountOperator="updateCountOperator"
      @updateCount="updateCount"
      @updateCumulative="updateCumulative"
    />
  </div>
</template>

<script>
import { get, isUndefined, keyBy, uniqBy } from 'lodash';

import HelpTooltip from '~lib/common/components/HelpTooltip';
import DosageCountCheck from './common/DosageCountCheck';

export default {
  name: 'oxygen-given-check',
  components: {
    HelpTooltip,
    DosageCountCheck,
  },
  props: [
    'oxygens',
    'readonly',
    'emitter',
    'ikey',
    'getData',
    'putData',
    'control',
  ],
  data() {
    return {
      state: {
        isCurrent: true,
        selectedOxygens: [],
        dosage: { min: null, max: null },
        isCumulative: false,
        count: null,
        countOperator: 'EQUAL',
      },
      value: 0,
    };
  },
  computed: {
    oxygensMap() {
      return keyBy(this.oxygens, 'id');
    },
    availableOxygens() {
      const { isGlobal } = this.emitter;
      if (isGlobal) {
        return this.oxygens;
      }
      return this.emitter.caseConf._oxygens.filter(x => get(x, 'options.enabled'))
        .map(x => this.oxygensMap[x.id]);
    },
    dosageAvailable() {
      return this.state.selectedOxygens && this.state.selectedOxygens.length === 1;
    },
    countAvailable() {
      return this.state.selectedOxygens && this.state.selectedOxygens.length <= 1;
    },
    warnings() {
      if (!this.emitter.$store) {
        return [];
      }
      const warnings = get(
        this.emitter.$store.getters['validation/visibleWarningsByFieldAndId'],
        `caseLogicDescription.${this.control.parent.id}`,
        [],
      ).filter(warning => warning.metadata && warning.metadata.checkKey === this.ikey);

      return uniqBy(warnings, 'message');
    },
    routeWarnings() {
      return this.warnings.filter(warning => !warning.metadata.countWarning);
    },
    countWarning() {
      return this.warnings.find(warning => warning.metadata.countWarning);
    },
  },
  beforeMount() {
    const data = this.getData(this.ikey);
    this.state.selectedOxygens = get(data, 'oxygens') || [];
    this.state.dosage = get(data, 'dosage') || { min: null, max: null };
    this.state.isCumulative = get(data, 'isCumulative') || false;
    this.state.count = get(data, 'count') || null;
    this.state.countOperator = get(data, 'countOperator') || 'EQUAL';
    this.state.isCurrent = isUndefined(data, 'isCurrent') ? true : data.isCurrent;
    this.update();
  },
  methods: {
    selectOxygens(oxygenKeys) {
      this.state.selectedOxygens = oxygenKeys;
      if (!this.dosageAvailable) {
        this.resetDosage();
      }
      if (!this.countAvailable) {
        this.resetCount();
      }
      this.update();
    },
    updateDosage(updates) {
      this.state.dosage = {
        ...this.state.dosage,
        ...updates,
      };
      this.update();
    },
    resetDosage() {
      this.updateDosage({ min: null, max: null });
      this.updateCumulative(false);
    },
    updateCount(count) {
      this.state.count = count;
      this.update();
    },
    resetCount() {
      this.state.count = null;
      this.state.countOperator = null;
      this.update();
    },
    updateCountOperator(op) {
      this.state.countOperator = op;
      this.update();
    },
    updateCumulative(isCumulative) {
      this.state.isCumulative = isCumulative;
      this.update();
    },
    updateCurrent(isCurrent) {
      this.state.isCurrent = isCurrent;
      this.update();
    },
    update() {
      this.putData(this.ikey, {
        type: 'OXYGEN_ADMINISTERED',
        id: 'OXYGEN',
        oxygens: this.state.selectedOxygens,
        dosage: this.state.dosage,
        isCumulative: this.state.isCumulative,
        count: this.state.count,
        countOperator: this.state.countOperator,
        operator: 'ADMINISTERED',
        value: true,
        isCurrent: this.state.isCurrent,
      });
      this.emitter.trigger('process', this.control);
    },
  },
};
</script>

<style scoped>
.oxygens-select-container {
  position: relative;
  flex: 1;
  display: flex;
}

.oxygen-oxygen-selects {
  display: flex;
}

.el-select {
  flex: 1;
}

.help-tooltip {
  position: absolute;
  right: 34px;
  top: 6px;
}


</style>
